/**
 * Where a value came from, in words (ORDR-119, FR-CFM-006, FR-CFM-013).
 *
 * Three origins, never an icon or a colour alone. The middle one is FR-CFM-013's
 * "defaults it never chose": an inherited value looks exactly like a chosen
 * one unless the surface says otherwise.
 *
 * `source_node_id: null` is NOT an empty value. It means nothing in the
 * ancestry sets the domain and built-in defaults apply, and it is said so.
 */

import type { ConfigReading } from "./configApi.js";

export function originSentence(reading: ConfigReading, nodeId: string, nodeName: string): string {
  if (reading.sourceNodeId === null) {
    return `Not set anywhere above ${nodeName}. Built-in defaults apply.`;
  }
  if (reading.sourceNodeId === nodeId) {
    return "Set here.";
  }
  return `Inherited from ${reading.sourceName ?? "another node"}. ${nodeName} never chose this.`;
}

/**
 * What a save does, said BEFORE it happens (item 5, HQ ruling C-HQ4).
 *
 * Resolution is WHOLE-DOMAIN: the nearest node holding a value supplies all
 * of it, with no key-level merge. So a save at a node that inherits stops it
 * following its ancestor for EVERY key in the domain, which is why the
 * inherited case needs a confirmation and the others do not.
 */
export function saveConsequence(
  reading: ConfigReading,
  nodeId: string,
  nodeName: string,
  domain: string,
): { readonly sentence: string | null; readonly needsConfirm: boolean } {
  if (reading.sourceNodeId === null) {
    return {
      sentence: `Saving here sets ${domain} for ${nodeName} and every location below it that does not set its own.`,
      needsConfirm: false,
    };
  }
  if (reading.sourceNodeId === nodeId) {
    return { sentence: null, needsConfirm: false };
  }
  const ancestor = reading.sourceName ?? "the node above it";
  return {
    sentence: `Saving here means ${nodeName} will stop following ${ancestor} for all ${domain} settings, not only the field you changed.`,
    needsConfirm: true,
  };
}
